const template = document.createElement("template");
template.innerHTML = `
<style>
  :host{
    display: block;
    font-family: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
  }

  .wrap{
    position: relative;
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    background: color-mix(in srgb, var(--ctl-color-border) 25%, var(--ctl-color-surface));
    overflow: hidden;
  }

  .header{
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--ctl-space-2) var(--ctl-space-3);
    border-bottom: 1px solid var(--ctl-color-border);
    background: color-mix(in srgb, var(--ctl-color-surface) 50%, color-mix(in srgb, var(--ctl-color-border) 25%, var(--ctl-color-surface)));
  }

  .header:empty{
    display: none;
  }

  .label{
    font-size: var(--ctl-font-size-sm);
    font-weight: var(--ctl-font-weight-bold);
    color: var(--ctl-color-muted);
    text-transform: uppercase;
    letter-spacing: var(--ctl-tracking-wide);
  }

  .label:empty{
    display: none;
  }

  .actions{
    display: flex;
    gap: var(--ctl-space-2);
  }

  .content{
    position: relative;
    overflow-x: auto;
    padding: var(--ctl-space-4);
  }

  pre{
    margin: 0;
    font-family: var(--ctl-font-mono, ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, Consolas, 'DejaVu Sans Mono', monospace);
    font-size: var(--font-size, var(--ctl-font-size-md));
    line-height: var(--ctl-leading-relaxed);
    color: var(--ctl-color-text);
    white-space: pre;
  }

  code{
    font-family: inherit;
    font-size: inherit;
    color: inherit;
  }

  /* line numbers */
  :host([line-numbers]) .content{
    padding-left: 0;
  }

  :host([line-numbers]) pre{
    counter-reset: line;
    padding-left: var(--ctl-space-7);
  }

  :host([line-numbers]) pre .line{
    display: block;
    position: relative;
  }

  :host([line-numbers]) pre .line::before{
    counter-increment: line;
    content: counter(line);
    position: absolute;
    left: calc(-1 * var(--ctl-space-6) - var(--ctl-space-3));
    width: var(--ctl-space-6);
    text-align: right;
    color: var(--ctl-color-muted);
    opacity: var(--ctl-opacity-muted);
    user-select: none;
  }

  /* sizes */
  :host([size="sm"]){
    --font-size: var(--ctl-font-size-sm);
  }

  :host(:not([size]), [size="md"]){
    --font-size: var(--ctl-font-size-md);
  }

  :host([size="lg"]){
    --font-size: var(--ctl-font-size-base);
  }

  /* compact padding */
  :host([compact]) .content{
    padding: var(--ctl-space-3) var(--ctl-space-3-5);
  }

  /* hide scrollbars in webkit */
  .content::-webkit-scrollbar{
    height: var(--ctl-space-2);
  }

  .content::-webkit-scrollbar-track{
    background: transparent;
  }

  .content::-webkit-scrollbar-thumb{
    background: color-mix(in srgb, var(--ctl-color-border) 60%, transparent);
    border-radius: var(--ctl-radius-sm);
  }

  .content::-webkit-scrollbar-thumb:hover{
    background: color-mix(in srgb, var(--ctl-color-border) 80%, transparent);
  }
</style>

<div class="wrap" part="wrap">
  <div class="header" part="header">
    <div class="label" part="label">
      <slot name="label"></slot>
    </div>
    <div class="actions" part="actions">
      <slot name="actions"></slot>
    </div>
  </div>
  <div class="content" part="content">
    <pre part="pre"><code id="code" part="code"><slot></slot></code></pre>
  </div>
</div>
`;

export class CtlCodeBlock extends HTMLElement {
  static get observedAttributes() {
    return ["language", "size", "compact", "line-numbers"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._code = this.shadowRoot.getElementById("code");
    this._lineNumberObserver = null;
  }

  connectedCallback() {
    // defaults
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    this._sync();
  }

  disconnectedCallback() {
    // Clean up observer to prevent memory leaks
    if (this._lineNumberObserver) {
      this._lineNumberObserver.disconnect();
      this._lineNumberObserver = null;
    }
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get language() { return this.getAttribute("language") || ""; }
  set language(v) { v ? this.setAttribute("language", String(v)) : this.removeAttribute("language"); }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) { this.setAttribute("size", String(v || "md")); }

  get compact() { return this.hasAttribute("compact"); }
  set compact(v) { v ? this.setAttribute("compact", "") : this.removeAttribute("compact"); }

  get lineNumbers() { return this.hasAttribute("line-numbers"); }
  set lineNumbers(v) { v ? this.setAttribute("line-numbers", "") : this.removeAttribute("line-numbers"); }

  // Get plain text content for copying
  get code() {
    return this.textContent?.trim() || "";
  }

  _sync() {
    const size = this.size;
    if (!["sm", "md", "lg"].includes(size)) {
      this.setAttribute("size", "md");
    }

    // If line numbers enabled, wrap each line
    if (this.lineNumbers) {
      this._applyLineNumbers();
    }
  }

  _applyLineNumbers() {
    // This is a simple implementation
    // For syntax highlighting, users would integrate a library like Prism or highlight.js
    const slot = this.shadowRoot.querySelector("slot:not([name])");
    if (!slot) return;

    // Disconnect previous observer if it exists to prevent memory leaks
    if (this._lineNumberObserver) {
      this._lineNumberObserver.disconnect();
    }

    this._lineNumberObserver = new MutationObserver(() => {
      this._formatLines();
    });

    this._lineNumberObserver.observe(this, {
      childList: true,
      characterData: true,
      subtree: true
    });

    this._formatLines();
  }

  _formatLines() {
    if (!this.lineNumbers) return;
    
    const text = this.textContent?.trim() || "";
    const lines = text.split("\n");
    
    // Create wrapped lines for line numbers
    const formatted = lines.map((line, i) => {
      return `<span class="line">${this._escapeHtml(line)}</span>`;
    }).join("\n");
    
    // Update code element with formatted content
    const slot = this._code.querySelector("slot");
    if (slot) {
      slot.style.display = "none";
    }
    this._code.innerHTML = formatted;
  }

  _escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define("ctl-code-block", CtlCodeBlock);
